// Vector Gantt PDF — Letter landscape (11x8.5), light theme, crisp text.
// The timeline is paginated horizontally into month windows so it always fits
// the page instead of being squeezed; rows paginate vertically. Projects,
// staff-group sub-headers, and members are visually distinct.

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

function hexToRgb(hex) {
  const h = (hex || "#666").replace("#", "");
  const v = h.length === 3 ? h.split("").map(c => c + c).join("") : h;
  return [parseInt(v.slice(0, 2), 16), parseInt(v.slice(2, 4), 16), parseInt(v.slice(4, 6), 16)];
}
function tint(hex, amt) {
  const [r, g, b] = hexToRgb(hex);
  return [Math.round(r + (255 - r) * amt), Math.round(g + (255 - g) * amt), Math.round(b + (255 - b) * amt)];
}

export async function exportGanttPdf({ title, subtitle, rows, monthStarts, tStart, tEnd, legend = [], fileName = "gantt.pdf" }) {
  const { jsPDF } = await import("jspdf");
  const PAGE_W = 792, PAGE_H = 612;          // Letter landscape (11 x 8.5 in @ 72pt/in)
  const margin = 28, labelW = 150;
  const chartX = margin + labelW;
  const chartR = PAGE_W - margin;
  const chartW = chartR - chartX;
  const topY = margin + 44;
  const firstRowY = topY + 12;
  const bottomY = PAGE_H - margin - 28;
  const MONTH_W = 46;
  const perPage = Math.max(1, Math.floor(chartW / MONTH_W));

  const pdf = new jsPDF({ orientation: "landscape", unit: "pt", format: [PAGE_W, PAGE_H] });
  let firstPage = true;

  const months = (monthStarts && monthStarts.length) ? monthStarts : [new Date(tStart)];
  const windows = [];
  for (let i = 0; i < months.length; i += perPage) windows.push(months.slice(i, i + perPage));
  const monthEnd = (d) => { const x = new Date(d); return new Date(x.getFullYear(), x.getMonth() + 1, 1); };

  windows.forEach((win, wi) => {
    const wStart = new Date(win[0]);
    const wEnd = monthEnd(win[win.length - 1]);
    const wWidth = win.length * MONTH_W;
    const wRight = chartX + wWidth;
    const span = Math.max(1, wEnd - wStart);
    const xOf = (d) => chartX + ((new Date(d) - wStart) / span) * wWidth;
    const clamp = (x) => Math.max(chartX, Math.min(wRight, x));
    let y = firstRowY;

    const drawLegend = () => {
      let lx = margin; const ly = PAGE_H - margin - 10;
      pdf.setFontSize(7.5);
      legend.forEach((it) => {
        const c = it.hatched ? tint(it.color, 0.55) : hexToRgb(it.color);
        pdf.setFillColor(c[0], c[1], c[2]); pdf.roundedRect(lx, ly - 7, 14, 8, 1.5, 1.5, "F");
        if (it.hatched) { const b = hexToRgb(it.color); pdf.setDrawColor(b[0], b[1], b[2]); pdf.setLineWidth(0.6); pdf.setLineDashPattern([2, 1], 0); pdf.roundedRect(lx, ly - 7, 14, 8, 1.5, 1.5, "S"); pdf.setLineDashPattern([], 0); }
        pdf.setTextColor(70, 76, 88); pdf.text(it.label, lx + 18, ly);
        lx += 18 + pdf.getTextWidth(it.label) + 14;
      });
    };

    const frame = () => {
      if (!firstPage) pdf.addPage([PAGE_W, PAGE_H], "landscape");
      firstPage = false;
      pdf.setFillColor(255, 255, 255); pdf.rect(0, 0, PAGE_W, PAGE_H, "F");
      pdf.setTextColor(20, 28, 46); pdf.setFont("helvetica", "bold"); pdf.setFontSize(13);
      pdf.text(title, margin, margin + 12);
      const lastM = new Date(win[win.length - 1]);
      pdf.setFont("helvetica", "normal"); pdf.setFontSize(8); pdf.setTextColor(120, 128, 140);
      pdf.text(`${subtitle}   ·   ${MONTHS[wStart.getMonth()]} ${wStart.getFullYear()} – ${MONTHS[lastM.getMonth()]} ${lastM.getFullYear()}   ·   sheet ${wi + 1} of ${windows.length}`, margin, margin + 26);
      pdf.setFontSize(7);
      win.forEach((m, k) => {
        const x = chartX + k * MONTH_W;
        pdf.setDrawColor(228, 230, 234); pdf.setLineWidth(0.5); pdf.line(x, topY, x, bottomY);
        pdf.setTextColor(130, 136, 148);
        const lbl = MONTHS[new Date(m).getMonth()] + (new Date(m).getMonth() === 0 ? " '" + String(new Date(m).getFullYear()).slice(2) : "");
        pdf.text(lbl, x + 2, topY - 3);
      });
      pdf.setDrawColor(170, 175, 184); pdf.setLineWidth(0.8);
      pdf.line(chartX, topY, chartX, bottomY);
      pdf.line(wRight, topY, wRight, bottomY);
      pdf.line(chartX, topY, wRight, topY);
      drawLegend();
      y = firstRowY;
    };

    const drawBars = (bars, by, bh) => {
      (bars || []).forEach((bar) => {
        const xs = xOf(bar.start), xe = xOf(bar.end);
        if (xe < chartX || xs > wRight) return; // entirely outside this window
        const x1 = clamp(xs), x2 = clamp(xe); const w = Math.max(2, x2 - x1);
        const yy = by + (bar.lane || 0) * (bh + 2);
        const base = hexToRgb(bar.color);
        if (bar.hatched) {
          const t = tint(bar.color, 0.55); pdf.setFillColor(t[0], t[1], t[2]); pdf.roundedRect(x1, yy, w, bh, 2, 2, "F");
          pdf.setDrawColor(base[0], base[1], base[2]); pdf.setLineWidth(0.7); pdf.setLineDashPattern([2.5, 1.5], 0); pdf.roundedRect(x1, yy, w, bh, 2, 2, "S"); pdf.setLineDashPattern([], 0);
        } else { pdf.setFillColor(base[0], base[1], base[2]); pdf.roundedRect(x1, yy, w, bh, 2, 2, "F"); }
        pdf.setFontSize(7);
        const txt = String(bar.label || ""); const tw = pdf.getTextWidth(txt);
        if (tw + 6 < w) { pdf.setTextColor(bar.hatched ? 40 : 255, bar.hatched ? 46 : 255, bar.hatched ? 58 : 255); pdf.text(txt, x1 + 4, yy + bh - 3); }
        else if (x2 + 4 + tw < wRight) { pdf.setTextColor(70, 76, 88); pdf.text(txt, x2 + 4, yy + bh - 3); }
      });
    };

    frame();

    rows.forEach((row) => {
      const kind = row.kind || "member";
      const lanes = (row.extraLanes || 0) + 1;
      const rowH = kind === "project" ? 28 : kind === "group" ? 18 : kind === "person" ? (lanes * 17 + 12) : 22;
      if (y + rowH > bottomY) frame();

      if (kind === "project") {
        pdf.setFillColor(231, 236, 243); pdf.rect(margin, y - 1, PAGE_W - margin * 2, rowH - 2, "F");
        if (row.accent) { const a = hexToRgb(row.accent); pdf.setFillColor(a[0], a[1], a[2]); pdf.rect(margin, y - 1, 4, rowH - 2, "F"); }
        pdf.setFont("helvetica", "bold"); pdf.setFontSize(9.5); pdf.setTextColor(20, 28, 46);
        pdf.text(String(row.label || "").slice(0, 34), margin + 10, y + 12);
        if (row.meta) { pdf.setFont("helvetica", "normal"); pdf.setFontSize(6.5); pdf.setTextColor(110, 116, 128); pdf.text(String(row.meta).slice(0, 44), margin + 10, y + 20); }
        drawBars(row.bars, y + 5, 13);
      } else if (kind === "group") {
        const c = hexToRgb(row.color || "#666");
        pdf.setFont("helvetica", "bold"); pdf.setFontSize(7); pdf.setTextColor(c[0], c[1], c[2]);
        pdf.text(String(row.label || "").toUpperCase(), margin + 16, y + 10);
      } else if (kind === "person") {
        pdf.setFont("helvetica", "bold"); pdf.setFontSize(9); pdf.setTextColor(25, 32, 50);
        pdf.text(String(row.label || "").slice(0, 30), margin, y + 11);
        if (row.meta) { pdf.setFont("helvetica", "normal"); pdf.setFontSize(6.5); pdf.setTextColor(140, 146, 158); pdf.text(String(row.meta).slice(0, 38), margin, y + 20); }
        drawBars(row.bars, y + 4, 13);
        pdf.setDrawColor(235, 238, 242); pdf.setLineWidth(0.5); pdf.line(margin, y + rowH - 3, chartR, y + rowH - 3);
      } else {
        pdf.setFont("helvetica", "normal"); pdf.setFontSize(8); pdf.setTextColor(60, 66, 78);
        pdf.text(String(row.label || "").slice(0, 28), margin + 22, y + 12);
        if (row.meta) { pdf.setFontSize(6); pdf.setTextColor(150, 156, 168); pdf.text(String(row.meta).slice(0, 34), margin + 22, y + 19); }
        drawBars(row.bars, y + 5, 13);
      }
      y += rowH;
    });
  });

  pdf.save(fileName);
}
